/**
 * Daily Self-Assessment Progress — Streak, rotation, and QAPI tracking.
 *
 * Provides metrics for the standalone dashboard:
 * - Streak: consecutive operating days with completed assessments
 * - Rotation progress: areas completed in current cycle
 * - Annual coverage: % of total items assessed this year
 * - Domain pass rates: feeds QAPI indicators
 */

import type { SurveyDomain } from './survey'
import { getRotationCycleLength } from './dailyAssessment'
import type { Visit } from './visits'

// ── Types ───────────────────────────────────────────────────────────────────

export interface DailyProgressSummary {
  streak: number
  rotationCompleted: number
  rotationTotal: number
  rotationPercent: number
  annualCompleted: number
  annualTotal: number
  annualPercent: number
}

export interface DomainPassRate {
  domain: SurveyDomain
  label: string
  passRate: number // 0-100, percentage of items rated green (1)
  totalItems: number
  status: 'on_track' | 'review' | 'at_risk'
}

// ── Domain labels ───────────────────────────────────────────────────────────

const DOMAIN_DISPLAY: Record<string, { label: string; icon: string }> = {
  IC: { label: 'Infection Control', icon: '🔬' },
  SPD: { label: 'Sterile Processing', icon: '🧪' },
  ENV: { label: 'Environment', icon: '🏥' },
  LSC: { label: 'Life Safety', icon: '🔥' },
  MED: { label: 'Medication Safety', icon: '💊' },
  COMP: { label: 'Compliance', icon: '📋' },
  HR: { label: 'Human Resources', icon: '👤' },
  CR: { label: 'Clinical Records', icon: '📁' },
  EM: { label: 'Emergency Mgmt', icon: '🚨' },
  ADA: { label: 'Accessibility', icon: '♿' },
  WASTE: { label: 'Medical Waste', icon: '⚠️' },
  WT: { label: 'Waived Testing', icon: '🧫' },
}

export function getDomainDisplay(domain: string): { label: string; icon: string } {
  return DOMAIN_DISPLAY[domain] ?? { label: domain, icon: '📌' }
}

// ── Progress calculations ───────────────────────────────────────────────────

/**
 * Calculate progress summary from daily assessment visits.
 * Daily assessment visits have visit_type containing 'daily' or status patterns
 * that distinguish them from full guided surveys.
 */
export function calculateProgress(
  allVisits: Visit[],
  operatingDaysThisYear: number,
): DailyProgressSummary {
  // Daily assessments are visits created by the daily flow
  // For now, count released visits as completed assessments
  const completedVisits = allVisits.filter(v => v.status === 'released')
  const cycleLength = getRotationCycleLength()

  // Streak: count consecutive recent completed visits
  // (simplified — true streak needs operating day continuity check)
  const streak = Math.min(completedVisits.length, operatingDaysThisYear)

  // Rotation: how many of the current cycle's areas have been done
  const rotationCompleted = completedVisits.length % cycleLength || (completedVisits.length > 0 ? cycleLength : 0)

  // Annual: total completed vs total operating days
  const annualPercent = operatingDaysThisYear > 0
    ? Math.round((completedVisits.length / operatingDaysThisYear) * 100)
    : 0

  return {
    streak,
    rotationCompleted: Math.min(rotationCompleted, cycleLength),
    rotationTotal: cycleLength,
    rotationPercent: Math.round((Math.min(rotationCompleted, cycleLength) / cycleLength) * 100),
    annualCompleted: completedVisits.length,
    annualTotal: operatingDaysThisYear,
    annualPercent: Math.min(annualPercent, 100),
  }
}

/**
 * Calculate QAPI domain pass rates from corrective action data.
 * Maps domain tags on actions to pass/fail rates.
 *
 * Pass rate logic:
 * - 90%+ green items = on_track
 * - 70-89% = review
 * - <70% = at_risk
 */
export function calculateDomainPassRates(
  overdueCount: number,
  openCount: number,
): DomainPassRate[] {
  // Until we have per-domain daily assessment response data persisted,
  // derive QAPI status from corrective action health
  const domains: SurveyDomain[] = ['IC', 'LSC', 'MED', 'ENV']

  return domains.map(domain => {
    const display = getDomainDisplay(domain)

    // Heuristic: if overdue actions exist, high-risk domains flag
    let status: DomainPassRate['status'] = 'on_track'
    let passRate = 95

    if (domain === 'LSC' && overdueCount > 0) {
      status = 'at_risk'
      passRate = Math.max(50, 95 - overdueCount * 15)
    } else if (domain === 'IC' && overdueCount > 0) {
      status = 'review'
      passRate = Math.max(70, 95 - overdueCount * 8)
    } else if (openCount > 3) {
      status = 'review'
      passRate = Math.max(75, 95 - openCount * 3)
    }

    return {
      domain,
      label: display.label,
      passRate,
      totalItems: 0, // populated when daily assessment responses are persisted
      status,
    }
  })
}

/**
 * Get approximate operating days elapsed this year.
 */
export function getOperatingDaysElapsed(): number {
  const now = new Date()
  const startOfYear = new Date(now.getFullYear(), 0, 1)
  let count = 0
  for (let d = new Date(startOfYear); d <= now; d.setDate(d.getDate() + 1)) {
    const day = d.getDay()
    if (day !== 0 && day !== 6) count++
  }
  return count
}
